import { webViewDirProps } from '@/core/utils/layoutDirection';
import React, { createContext, useContext, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import type { BloomUniversalSpaceContent } from '../types/bloomUniversalContent';
import { BloomSpaceCard } from './BloomSpaceCard';

const MosaicChromeContext = createContext<{ arrowIconUrl?: string }>({});

function useMosaicArrowIconUrl(): string | undefined {
  return useContext(MosaicChromeContext).arrowIconUrl;
}

const DEFAULT_GUTTER = 8;
/** Default phone tile heights (Figma mobile mosaic). */
const DEFAULT_ROW_CARD_HEIGHT = 118;
const DEFAULT_BOTTOM_CARD_HEIGHT = 118;

/**
 * Figma mobile tall Facilities card:
 * 146×208 · Spacing/2 inset · gap 62 · Size8 radius · 1px border
 */
export const PHONE_TALL_FACILITIES_CARD = {
  width: 146,
  height: 208,
  inset: 8,
  titleArrowGap: 62,
  radius: 8,
} as const;

/** Phone + medium bentos render the first six hub spaces (by CMS sortOrder). */
const BENTO_SLOT_COUNT = 6;

export type BloomMosaicVariant = 'phone' | 'medium';

function sortHubSpacesByOrder(
  spaces: readonly BloomUniversalSpaceContent[],
): BloomUniversalSpaceContent[] {
  return [...spaces].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.spaceKey.localeCompare(b.spaceKey),
  );
}

function bentoSlotAt(
  ordered: readonly BloomUniversalSpaceContent[],
  index: number,
): BloomUniversalSpaceContent | undefined {
  return ordered[index];
}

export interface BloomUniversalPhoneMosaicProps {
  readonly spaces: readonly BloomUniversalSpaceContent[];
  readonly isRTL: boolean;
  readonly onOpenSpace: (spaceKey: string) => void;
  /** Gap between tiles — larger on medium web. */
  readonly gutter?: number;
  /** Hospitality / Properties / Retail row height. */
  readonly rowCardHeight?: number;
  /** Education / Landscape row height (phone). Medium uses rowCardHeight + fullRowHeight. */
  readonly bottomCardHeight?: number;
  /** Hub mosaic title size override (medium web). */
  readonly titleFontSize?: number;
  /** Action-card inner inset override (medium web). */
  readonly contentInset?: number;
  /** Contentful hub `arrowIcon` — falls back to app arrow. */
  readonly arrowIconUrl?: string;
  /**
   * `phone` / `medium` — six-slot bento; slot assignment follows hub `sortOrder`.
   */
  readonly variant?: BloomMosaicVariant;
}

/** Total mosaic height for phone bento layout math. */
export function getBloomBentoMosaicHeight(
  rowCardHeight: number,
  bottomCardHeight: number,
  gutter: number,
): number {
  const tallHeight = rowCardHeight * 2 + gutter;
  return tallHeight + gutter + bottomCardHeight;
}

/**
 * Medium web mosaic beside a tall left card:
 * rightCol = row + mid + row + 2*gaps
 */
export function getMediumMosaicHeight(
  rowCardHeight: number,
  midRowHeight: number,
  gutter: number,
): number {
  return rowCardHeight * 2 + midRowHeight + gutter * 2;
}

function resolveTileFlexStyle(
  fixedWidth: number | undefined,
  flex: number,
): object {
  if (fixedWidth != null) {
    return {
      width: fixedWidth,
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: fixedWidth,
    };
  }
  if (flex === 0) {
    return {
      width: '100%' as const,
      flexGrow: 0,
      flexShrink: 0,
    };
  }
  return {
    flexGrow: flex,
    flexShrink: 1,
    flexBasis: 0,
  };
}

type SpaceTileOptionalCardProps = {
  titleFontSize?: number;
  contentInset?: number;
  titleTopPercent?: number;
  titleMaxLines?: number;
  titleArrowGap?: number;
  footerBottomInset?: number;
  titlePlacement?: 'mid' | 'bottom';
  borderRadius?: number;
  tileImageUrl?: string;
  iconUrl?: string;
  arrowIconUrl?: string;
};

function pickOptionalNumberProp(
  key: 'titleFontSize' | 'contentInset' | 'titleTopPercent' | 'titleMaxLines' | 'titleArrowGap' | 'footerBottomInset' | 'borderRadius',
  value: number | undefined,
): SpaceTileOptionalCardProps {
  if (value == null) {
    return {};
  }
  return { [key]: value };
}

/** Flatten optional BloomSpaceCard props — keeps SpaceTile under Sonar complexity. */
function buildSpaceTileOptionalCardProps(
  space: BloomUniversalSpaceContent,
  options: Readonly<
    Partial<{
      titleFontSize: number;
      contentInset: number;
      titleTopPercent: number;
      titleMaxLines: number;
      titleArrowGap: number;
      footerBottomInset: number;
      titlePlacement: 'mid' | 'bottom';
      borderRadius: number;
      arrowIconUrl: string;
    }>
  >,
): SpaceTileOptionalCardProps {
  return {
    ...pickOptionalNumberProp('titleFontSize', options.titleFontSize),
    ...pickOptionalNumberProp('contentInset', options.contentInset),
    ...pickOptionalNumberProp('titleTopPercent', options.titleTopPercent),
    ...pickOptionalNumberProp('titleMaxLines', options.titleMaxLines),
    ...pickOptionalNumberProp('titleArrowGap', options.titleArrowGap),
    ...pickOptionalNumberProp('footerBottomInset', options.footerBottomInset),
    ...pickOptionalNumberProp('borderRadius', options.borderRadius),
    ...(options.titlePlacement != null
      ? { titlePlacement: options.titlePlacement }
      : {}),
    ...(space.tileImageUrl ? { tileImageUrl: space.tileImageUrl } : {}),
    ...(space.iconUrl ? { iconUrl: space.iconUrl } : {}),
    ...(options.arrowIconUrl ? { arrowIconUrl: options.arrowIconUrl } : {}),
  };
}

function SpaceTile({
  space,
  height,
  onOpenSpace,
  titleFontSize,
  contentInset,
  flex = 1,
  fixedWidth,
  titleTopPercent,
  titleMaxLines,
  titleArrowGap,
  footerBottomInset,
  titlePlacement,
  borderRadius,
  arrowIconUrl: arrowIconUrlProp,
}: Readonly<{
  space: BloomUniversalSpaceContent | undefined;
  height: number;
  onOpenSpace: (spaceKey: string) => void;
  titleFontSize?: number;
  contentInset?: number;
  flex?: number;
  fixedWidth?: number;
  titleTopPercent?: number;
  titleMaxLines?: number;
  titleArrowGap?: number;
  footerBottomInset?: number;
  titlePlacement?: 'mid' | 'bottom';
  borderRadius?: number;
  arrowIconUrl?: string;
}>) {
  const contextArrowIconUrl = useMosaicArrowIconUrl();
  const arrowIconUrl = arrowIconUrlProp ?? contextArrowIconUrl;
  if (!space) {
    return (
      <View
        style={[
          styles.tileSlot,
          { height, flex: fixedWidth != null ? 0 : flex },
          fixedWidth != null ? { width: fixedWidth } : null,
        ]}
      />
    );
  }

  const optionalCardProps = buildSpaceTileOptionalCardProps(space, {
    ...(titleFontSize != null ? { titleFontSize } : {}),
    ...(contentInset != null ? { contentInset } : {}),
    ...(titleTopPercent != null ? { titleTopPercent } : {}),
    ...(titleMaxLines != null ? { titleMaxLines } : {}),
    ...(titleArrowGap != null ? { titleArrowGap } : {}),
    ...(footerBottomInset != null ? { footerBottomInset } : {}),
    ...(titlePlacement != null ? { titlePlacement } : {}),
    ...(borderRadius != null ? { borderRadius } : {}),
    ...(arrowIconUrl ? { arrowIconUrl } : {}),
  });

  return (
    <View
      style={[
        styles.tileSlot,
        fixedWidth != null ? styles.tileSlotFixed : null,
        {
          height,
          ...resolveTileFlexStyle(fixedWidth, flex),
        },
      ]}
    >
      <BloomSpaceCard
        variant="action"
        spaceKey={space.spaceKey}
        title={space.title}
        height={height}
        width={fixedWidth ?? '100%'}
        gutter={0}
        {...optionalCardProps}
        onPress={() => onOpenSpace(space.spaceKey)}
      />
    </View>
  );
}

/**
 * Phone mosaic title size from tile footprint.
 * Tall card is only ~146px wide — keep type readable without ellipsis.
 * Half tiles stay ≥13px; wide tiles scale with height.
 */
function resolvePhoneTileTitleSize(
  height: number,
  widthTier: 'tall' | 'wide' | 'half',
  _titleLength: number,
): number {
  if (widthTier === 'tall') {
    /** Narrow tall tile: 15–17 so “Bloom Facilities” wraps cleanly (2 lines). */
    const t = Math.min(1, Math.max(0, (height - 180) / 60));
    return Math.round(15 + t * 2);
  }
  const heightScale = Math.min(1.1, Math.max(0.9, height / DEFAULT_ROW_CARD_HEIGHT));
  if (widthTier === 'half') {
    return Math.max(13, Math.min(15, Math.round(14 * heightScale)));
  }
  return Math.max(14, Math.min(17, Math.round(15 * heightScale)));
}

/**
 * Medium mosaic title size.
 * The tall card uses the same size as the wide row so the labels match.
 * The 3-up narrow row stays at the hub base.
 */
function resolveMediumTileTitleSize(
  height: number,
  widthTier: 'wide' | 'narrow',
  hubBase: number,
): number {
  if (widthTier === 'wide') {
    const t = Math.min(1, Math.max(0, (height - 90) / 80));
    return Math.max(hubBase, Math.round(hubBase + 1 + t * 2));
  }
  return Math.max(12, hubBase);
}

function PhoneBentoMosaic({
  ordered,
  isRTL,
  onOpenSpace,
  gutter,
  rowCardHeight: _rowCardHeight,
  bottomCardHeight,
  titleFontSize,
}: Readonly<{
  ordered: readonly BloomUniversalSpaceContent[];
  isRTL: boolean;
  onOpenSpace: (spaceKey: string) => void;
  gutter: number;
  rowCardHeight: number;
  bottomCardHeight: number;
  titleFontSize?: number;
}>) {
  const tallCardHeight = PHONE_TALL_FACILITIES_CARD.height;
  const tallRowHeight = Math.max(
    56,
    Math.round((tallCardHeight - gutter) / 2),
  );
  const rowDir = isRTL ? 'row-reverse' : 'row';
  const cardTitleSize = titleFontSize ?? 14;
  const tallTitle = cardTitleSize;
  const wideTitle = cardTitleSize;
  const halfPropsTitle = cardTitleSize;
  const halfRetailTitle = cardTitleSize;
  const eduTitle = cardTitleSize;
  const landTitle = cardTitleSize;

  return (
    <>
      <View
        style={[
          styles.row,
          styles.topBentoRow,
          { flexDirection: rowDir, gap: gutter, height: tallCardHeight },
        ]}
      >
        <SpaceTile
          space={bentoSlotAt(ordered, 0)}
          height={PHONE_TALL_FACILITIES_CARD.height}
          flex={0.92}
          onOpenSpace={onOpenSpace}
          titleFontSize={tallTitle}
          contentInset={PHONE_TALL_FACILITIES_CARD.inset}
          titleArrowGap={PHONE_TALL_FACILITIES_CARD.titleArrowGap}
          footerBottomInset={PHONE_TALL_FACILITIES_CARD.inset + 10}
          borderRadius={PHONE_TALL_FACILITIES_CARD.radius}
          titlePlacement="mid"
          titleMaxLines={2}
        />
        <View
          style={[
            styles.rightColumn,
            { gap: gutter, height: tallCardHeight },
          ]}
        >
          <SpaceTile
            space={bentoSlotAt(ordered, 1)}
            height={tallRowHeight}
            flex={0}
            onOpenSpace={onOpenSpace}
            titleFontSize={wideTitle}
            titlePlacement="mid"
            titleTopPercent={0.45}
            titleMaxLines={2}
          />
          <View
            style={[
              styles.row,
              styles.halfRow,
              { flexDirection: rowDir, gap: gutter, height: tallRowHeight },
            ]}
          >
            <SpaceTile
              space={bentoSlotAt(ordered, 2)}
              height={tallRowHeight}
              onOpenSpace={onOpenSpace}
              titleFontSize={halfPropsTitle}
              titlePlacement="mid"
              titleTopPercent={0.45}
              titleMaxLines={2}
            />
            <SpaceTile
              space={bentoSlotAt(ordered, 3)}
              height={tallRowHeight}
              onOpenSpace={onOpenSpace}
              titleFontSize={halfRetailTitle}
              titlePlacement="mid"
              titleTopPercent={0.45}
              titleMaxLines={2}
            />
          </View>
        </View>
      </View>

      <View
        style={[
          styles.row,
          styles.bottomBentoRow,
          { flexDirection: rowDir, gap: gutter, height: bottomCardHeight },
        ]}
      >
        <SpaceTile
          space={bentoSlotAt(ordered, 4)}
          height={bottomCardHeight}
          onOpenSpace={onOpenSpace}
          titleFontSize={eduTitle}
          titlePlacement="mid"
          titleTopPercent={0.45}
          titleMaxLines={2}
        />
        <SpaceTile
          space={bentoSlotAt(ordered, 5)}
          height={bottomCardHeight}
          flex={1.35}
          onOpenSpace={onOpenSpace}
          titleFontSize={landTitle}
          titlePlacement="mid"
          titleTopPercent={0.45}
          titleMaxLines={2}
        />
      </View>
    </>
  );
}

/**
 * Medium web (slots 0–5 = hub sortOrder 1–6):
 * [ 0 tall ] [ 1 wide top ]
 *            [ 2 | 3 | 4 mid ]
 *            [ 5 wide bottom ]
 */
function MediumWebMosaic({
  ordered,
  isRTL,
  onOpenSpace,
  gutter,
  rowCardHeight,
  midRowHeight,
  titleFontSize,
  contentInset = 12,
}: Readonly<{
  ordered: readonly BloomUniversalSpaceContent[];
  isRTL: boolean;
  onOpenSpace: (spaceKey: string) => void;
  gutter: number;
  rowCardHeight: number;
  midRowHeight: number;
  titleFontSize?: number;
  contentInset?: number;
}>) {
  const rowDir = isRTL ? 'row-reverse' : 'row';
  const hubBase = titleFontSize ?? 16;
  const tallCardHeight = rowCardHeight * 2 + midRowHeight + gutter * 2;
  const wideTitle = resolveMediumTileTitleSize(rowCardHeight, 'wide', hubBase);
  const narrowTitle = resolveMediumTileTitleSize(
    midRowHeight,
    'narrow',
    hubBase,
  );
  const tileBase = { contentInset, titleMaxLines: 2 };
  const midTitle = {
    titlePlacement: 'mid' as const,
    titleTopPercent: 0.45,
  };
  const bottomTitle = { titlePlacement: 'bottom' as const };

  return (
    <View style={[styles.row, { flexDirection: rowDir, gap: gutter }]}>
      {/* Tall card uses the same bottom title + arrow row as the other tiles. */}
      <SpaceTile
        space={bentoSlotAt(ordered, 0)}
        height={tallCardHeight}
        onOpenSpace={onOpenSpace}
        flex={0.7}
        titleFontSize={wideTitle}
        {...bottomTitle}
        {...tileBase}
      />

      <View style={[styles.rightColumn, { gap: gutter, flex: 1.3 }]}>
        <SpaceTile
          space={bentoSlotAt(ordered, 1)}
          height={rowCardHeight}
          onOpenSpace={onOpenSpace}
          flex={1}
          titleFontSize={wideTitle}
          {...bottomTitle}
          {...tileBase}
        />

        <View style={[styles.row, { flexDirection: rowDir, gap: gutter }]}>
          <SpaceTile
            space={bentoSlotAt(ordered, 2)}
            height={midRowHeight}
            onOpenSpace={onOpenSpace}
            flex={1}
            titleFontSize={narrowTitle}
            {...midTitle}
            {...tileBase}
          />
          <SpaceTile
            space={bentoSlotAt(ordered, 3)}
            height={midRowHeight}
            onOpenSpace={onOpenSpace}
            flex={1}
            titleFontSize={narrowTitle}
            {...midTitle}
            {...tileBase}
          />
          <SpaceTile
            space={bentoSlotAt(ordered, 4)}
            height={midRowHeight}
            onOpenSpace={onOpenSpace}
            flex={1}
            titleFontSize={narrowTitle}
            {...midTitle}
            {...tileBase}
          />
        </View>

        <SpaceTile
          space={bentoSlotAt(ordered, 5)}
          height={rowCardHeight}
          onOpenSpace={onOpenSpace}
          flex={1}
          titleFontSize={wideTitle}
          {...bottomTitle}
          {...tileBase}
        />
      </View>
    </View>
  );
}

/**
 * Hub mosaic:
 * - phone / medium: six-slot bento filled by hub `sortOrder`
 */
export function BloomUniversalPhoneMosaic({
  spaces,
  isRTL,
  onOpenSpace,
  gutter = DEFAULT_GUTTER,
  rowCardHeight = DEFAULT_ROW_CARD_HEIGHT,
  bottomCardHeight = DEFAULT_BOTTOM_CARD_HEIGHT,
  titleFontSize,
  contentInset,
  arrowIconUrl,
  variant = 'phone',
}: BloomUniversalPhoneMosaicProps) {
  const ordered = useMemo(() => sortHubSpacesByOrder(spaces), [spaces]);
  const overflow = useMemo(
    () => ordered.slice(BENTO_SLOT_COUNT),
    [ordered],
  );

  const midRowHeight = Math.max(
    72,
    Math.round(rowCardHeight * 0.72),
  );

  const chromeValue = useMemo(
    () => (arrowIconUrl ? { arrowIconUrl } : {}),
    [arrowIconUrl],
  );

  let mosaicBody: React.ReactNode;
  if (variant === 'medium') {
    const mediumProps: {
      titleFontSize?: number;
      contentInset?: number;
    } = {};
    if (titleFontSize != null) {
      mediumProps.titleFontSize = titleFontSize;
    }
    if (contentInset != null) {
      mediumProps.contentInset = contentInset;
    }
    mosaicBody = (
      <MediumWebMosaic
        ordered={ordered}
        isRTL={isRTL}
        onOpenSpace={onOpenSpace}
        gutter={gutter}
        rowCardHeight={rowCardHeight}
        midRowHeight={midRowHeight}
        {...mediumProps}
      />
    );
  } else {
    const phoneProps: { titleFontSize?: number } = {};
    if (titleFontSize != null) {
      phoneProps.titleFontSize = titleFontSize;
    }
    mosaicBody = (
      <PhoneBentoMosaic
        ordered={ordered}
        isRTL={isRTL}
        onOpenSpace={onOpenSpace}
        gutter={gutter}
        rowCardHeight={rowCardHeight}
        bottomCardHeight={bottomCardHeight}
        {...phoneProps}
      />
    );
  }

  return (
    <MosaicChromeContext.Provider value={chromeValue}>
      <View
        style={[styles.root, { gap: gutter }]}
        {...(isRTL ? webViewDirProps('ltr') : {})}
        accessibilityLabel="Bloom spaces"
      >
        {mosaicBody}

        {overflow.map((space) => (
          <SpaceTile
            key={space.spaceKey}
            space={space}
            height={rowCardHeight}
            onOpenSpace={onOpenSpace}
            {...(titleFontSize != null ? { titleFontSize } : {})}
          />
        ))}
      </View>
    </MosaicChromeContext.Provider>
  );
}

const styles = StyleSheet.create({
  root: {
    width: '100%',
  },
  row: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  topBentoRow: {
    width: '100%',
  },
  halfRow: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
  },
  bottomBentoRow: {
    width: '100%',
  },
  rightColumn: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    justifyContent: 'flex-start',
  },
  /**
   * Do not clip here. A square overflow on the slot cuts the rounded card
   * and leaves a grey corner on mid-size screens.
   */
  tileSlot: {
    minWidth: 0,
    overflow: 'visible',
    backgroundColor: 'transparent',
  },
  tileSlotFixed: {
    flexGrow: 0,
    flexShrink: 0,
  },
});

export default BloomUniversalPhoneMosaic;
